import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { SysMessageItem } from '@/types/mqtt'
import { handleSysMessageJump } from '@/utils/mqtt/jump'
import SysMessageStorage from '@/utils/sysMessageStorage'
import { useAuthStore } from '@/stores/auth'

export const useSysMessageStore = defineStore('sysMessage', () => {
  const queue = ref<SysMessageItem[]>([])
  const messages = ref<SysMessageItem[]>([])
  const listDialogVisible = ref(false)
  const currentUid = ref<number | null>(null)

  const currentMessage = computed(() => queue.value[0] ?? null)
  const hasMessage = computed(() => queue.value.length > 0)
  const unreadCount = computed(() => messages.value.filter(item => !item.read).length)

  const resolveUid = (uid?: number | null) => {
    if (uid) return uid
    const authStore = useAuthStore()
    return authStore.userId ?? authStore.userInfo?.uid ?? null
  }

  const loadHistory = (uid?: number | null) => {
    const targetUid = resolveUid(uid)
    if (!targetUid) {
      messages.value = []
      currentUid.value = null
      return
    }

    currentUid.value = targetUid
    messages.value = SysMessageStorage.getMessages(targetUid)
  }

  const persistMessage = (message: SysMessageItem, uid?: number | null) => {
    const targetUid = resolveUid(uid)
    if (!targetUid) return

    currentUid.value = targetUid
    messages.value = SysMessageStorage.appendMessage(targetUid, message)
  }

  const enqueue = (message: SysMessageItem) => {
    const existsInQueue = queue.value.some(item => item.id === message.id)
    if (!existsInQueue) {
      queue.value.push(message)
    }

    persistMessage(message)
  }

  const markAsRead = (messageId: string) => {
    const targetUid = resolveUid(currentUid.value)
    if (!targetUid) return

    messages.value = SysMessageStorage.markAsRead(targetUid, messageId)
    queue.value = queue.value.map(item =>
      item.id === messageId ? { ...item, read: true } : item
    )
  }

  const markAllAsRead = () => {
    const targetUid = resolveUid(currentUid.value)
    if (!targetUid) return

    messages.value = SysMessageStorage.markAllAsRead(targetUid)
  }

  const removeMessage = (messageId: string) => {
    const targetUid = resolveUid(currentUid.value)
    if (!targetUid) return

    messages.value = SysMessageStorage.removeMessage(targetUid, messageId)
    queue.value = queue.value.filter(item => item.id !== messageId)
  }

  const clearHistory = () => {
    const targetUid = resolveUid(currentUid.value)
    if (!targetUid) return

    SysMessageStorage.clearMessages(targetUid)
    messages.value = []
  }

  const closeCurrent = () => {
    const message = queue.value[0]
    if (message) {
      markAsRead(message.id)
    }
    queue.value.shift()
  }

  const handleAction = () => {
    const message = currentMessage.value
    if (message?.attachment) {
      handleSysMessageJump(message.attachment)
    }
    closeCurrent()
  }

  const clearPopupQueue = () => {
    queue.value = []
  }

  const openListDialog = () => {
    loadHistory()
    listDialogVisible.value = true
  }

  const closeListDialog = () => {
    listDialogVisible.value = false
  }

  return {
    queue,
    messages,
    listDialogVisible,
    currentMessage,
    hasMessage,
    unreadCount,
    loadHistory,
    enqueue,
    markAsRead,
    markAllAsRead,
    removeMessage,
    clearHistory,
    closeCurrent,
    handleAction,
    clearPopupQueue,
    openListDialog,
    closeListDialog
  }
})
